import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import Client from '@spike/client-model';
import clsx from 'clsx';
import { FunctionComponent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { ClientProfileOverview } from '@spike/client-model';

interface ChatHeaderProps {
    client?: Client;
    onBackButton: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            alignItems: 'center',
            borderBottom: '#D4D4D4 1px solid',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            padding: '16px 24px;',
            width: '100%'
        },
        noClient: {
            justifyContent: 'flex-end',
            borderBottom: 'none'
        },
        clientName: {
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '100%'
        },
        nameRow: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'nowrap',
            minWidth: 0
        },
        nameText: {
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        owesPill: {
            color: '#CC444B',
            fontSize: 13,
            fontWeight: 500,
            borderRadius: 100,
            padding: '4px 10px',
            backgroundColor: '#FFE5E7',
            lineHeight: '20px',
            display: 'inline-flex',
            alignItems: 'center',
            position: 'relative',
            top: -2
        },
        backButton: {
            cursor: 'pointer'
        },
        buttonContainer: {
            backgroundColor: '#F1F1F1',
            borderRadius: '38px',
            textAlign: 'center',
            height: '38px',
            paddingTop: '6px',
            width: '38px'
        }
    })
);

const ChatHeader: FunctionComponent<ChatHeaderProps> = props => {
    const classes = useStyles();
    const profileOverview = useSelector<RootState, ClientProfileOverview | undefined>(
        state => state.clients.profileOverview
    );

    const parseMoney = (value: unknown): number => {
        if (typeof value === 'number') return value;
        if (typeof value !== 'string') return Number.NaN;
        return parseFloat(value.replace(/[^0-9.-]+/g, ''));
    };

    const getProfileCustomerId = (overview: ClientProfileOverview | undefined): number | undefined => {
        const anyOverview = overview as any;
        return (
            anyOverview?.customer?.id ??
            anyOverview?.customerId ??
            anyOverview?.customer?.customerId ??
            anyOverview?.customer?.clientId
        );
    };

    const clientOwes = useMemo(() => {
        if (!props.client?.id || !profileOverview) return false;

        const overviewCustomerId = getProfileCustomerId(profileOverview);
        if (overviewCustomerId != null && overviewCustomerId !== props.client.id) return false;

        const debt = (profileOverview as any)?.outstandingInvoices?.invoiceDebt;
        const amount = parseMoney(debt);
        return !Number.isNaN(amount) && amount > 0;
    }, [props.client?.id, profileOverview]);

    return (
        <div className={clsx(classes.container, !props.client && classes.noClient)}>
            {props.client && (
                <div>
                    <span className={clsx(classes.clientName, classes.nameRow)}>
                        <span className={classes.nameText}>{`${props.client.firstName} ${props.client.lastName}`}</span>
                        {clientOwes && <span className={classes.owesPill}>Owes</span>}
                    </span>
                </div>
            )}

            <div className={classes.buttonContainer}>
                <FontAwesomeIcon
                    icon={faChevronRight}
                    size="sm"
                    onClick={() => props.onBackButton && props.onBackButton()}
                    className={classes.backButton}
                />
            </div>
        </div>
    );
};

export default ChatHeader;
